import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTheme } from "@/components/ThemeProvider";
import { Logo } from "./Logo";
import { CreditCard, BookOpen, AlertTriangle, Settings } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";

interface RestrictedMobileMenuProps {
  onOpenChange: (open: boolean) => void;
}

interface MobileNavLinkProps {
  to: string;
  title: string;
  active: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
}

function MobileNavLink({ to, title, active, onClick, icon }: MobileNavLinkProps) {
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] border border-transparent hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-soft ${
          isActive 
            ? "bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 shadow-inner-soft border-primary-200 dark:border-primary-700" 
            : is24Seven 
              ? "text-24seven-text hover:bg-gradient-to-r hover:from-primary-50 hover:to-neutral-50 hover:text-primary-700" 
              : "text-muted-foreground hover:bg-gradient-to-r hover:from-primary-50 hover:to-neutral-50 hover:text-primary-700"
        }`
      }
    >
      {icon && (
        <div className="mr-3 p-1 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 group-hover:from-primary-200 group-hover:to-primary-300 dark:group-hover:from-primary-800/50 dark:group-hover:to-primary-700/50 transition-all duration-200">
          <span className="text-primary-600 dark:text-primary-400">{icon}</span>
        </div>
      )}
      <span className="font-medium">{title}</span>
    </NavLink>
  );
}

/**
 * Mobile menu for users without a valid paid subscription.
 * Shows only subscription and help options.
 */
export function RestrictedMobileMenu({ onOpenChange }: RestrictedMobileMenuProps) {
  const location = useLocation();
  const { t } = useTranslation();
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-2">
        <Logo isAuthenticated={true} />
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <div className="space-y-6 py-6">
          {/* Alert Section */}
          <div className="px-4">
            <Alert className="border-warning/50 bg-warning/10">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-sm">
                {t('subscription.accessRestricted', 'Please complete your subscription to access all features.')}
              </AlertDescription>
            </Alert>
          </div>

          {/* Subscription Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg">
                <CreditCard className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                {t('nav.getStarted', 'Get Started')}
              </h2>
            </div>
            <div className="space-y-2">
              <MobileNavLink 
                to="/settings/subscription" 
                title={t('nav.choosePlan', 'Choose a Plan')}
                active={isActive("/settings/subscription")} 
                onClick={() => onOpenChange(false)}
                icon={<CreditCard className="h-4 w-4" />}
              />
            </div>
          </div>
          
          <div className="px-4">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-600 to-transparent"></div>
          </div>
          
          {/* Help Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                <BookOpen className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                {t('nav.help', 'Help')}
              </h2>
            </div>
            <div className="space-y-2">
              <MobileNavLink 
                to="/documentation" 
                title={t('nav.documentation', 'Documentation')}
                active={isActive("/documentation")} 
                onClick={() => onOpenChange(false)}
                icon={<BookOpen className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/settings" 
                title={t('nav.settings', 'Settings')}
                active={location.pathname === "/settings"} 
                onClick={() => onOpenChange(false)}
                icon={<Settings className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
